/**
 * Scouting Events Logger (SE-4010)
 *
 * Handles logging of scouting-workflow events to PostHog Analytics:
 * kanban pipeline, match intake, scout reports, shadow team, and
 * knowledge graph (Ask Gemini).
 * @module features/analytics/utils/scoutingEventLogger
 */
import { ANALYTICS_EVENTS } from "../constants";
import type {
  IScoutingAnalyticsService,
  TKanbanBoardViewedParams,
  TKnowledgeGraphOpenedParams,
  TMatchIntakeStartedParams,
  TReportOpenedParams,
  TShadowTeamViewedParams,
} from "../types";
import { sendAnalyticsEvent } from "./posthogSender";

/**
 * Scouting events analytics logger.
 *
 * Provides methods for logging engagement with scouting-workflow surfaces.
 * @example
 * ```typescript
 * import { AnalyticsTracker } from '@/features/analytics';
 *
 * AnalyticsTracker.scouting.logKanbanBoardViewed({ board_id: 'board-123' });
 * ```
 */
export const scoutingEventLogger: IScoutingAnalyticsService = {
  /**
   * Logs a kanban board viewed event.
   * @param params - Kanban board viewed parameters
   */
  logKanbanBoardViewed: async (
    params: TKanbanBoardViewedParams
  ): Promise<void> => {
    await sendAnalyticsEvent(
      ANALYTICS_EVENTS.SCOUTING.KANBAN_BOARD_VIEWED,
      params
    );
  },

  /**
   * Logs a match intake started event.
   * @param params - Match intake started parameters
   */
  logMatchIntakeStarted: async (
    params: TMatchIntakeStartedParams
  ): Promise<void> => {
    await sendAnalyticsEvent(
      ANALYTICS_EVENTS.SCOUTING.MATCH_INTAKE_STARTED,
      params
    );
  },

  /**
   * Logs a report opened event.
   * @param params - Report opened parameters
   */
  logReportOpened: async (params: TReportOpenedParams): Promise<void> => {
    await sendAnalyticsEvent(ANALYTICS_EVENTS.SCOUTING.REPORT_OPENED, params);
  },

  /**
   * Logs a shadow team viewed event.
   * @param params - Shadow team viewed parameters
   */
  logShadowTeamViewed: async (
    params: TShadowTeamViewedParams
  ): Promise<void> => {
    await sendAnalyticsEvent(
      ANALYTICS_EVENTS.SCOUTING.SHADOW_TEAM_VIEWED,
      params
    );
  },

  /**
   * Logs a knowledge graph opened event.
   * @param params - Knowledge graph opened parameters
   */
  logKnowledgeGraphOpened: async (
    params: TKnowledgeGraphOpenedParams
  ): Promise<void> => {
    await sendAnalyticsEvent(
      ANALYTICS_EVENTS.SCOUTING.KNOWLEDGE_GRAPH_OPENED,
      params
    );
  },
};
